import { GetWorkSpaceAccessTokenUseCase } from "@/core/usecases/workspace";
import { AuthRepository } from "@/infrastructure/repositories/auth";
import { WorkSpaceRepository } from "@/infrastructure/repositories/workspace";
import { AppConfig } from "@/infrastructure/services/config";
import { createLogger } from "@/infrastructure/services/logger";
import { useEffect, useRef, useState, useCallback } from "react";

interface UseSocketListenerOptions {
  onProcessed?: (data: string) => void;
  enabled?: boolean;
}

const getWorkSpaceAccessToken = async (): Promise<string> => {
  const workSpaceAccessToken = await new GetWorkSpaceAccessTokenUseCase(new WorkSpaceRepository(new AuthRepository())).execute();
  return workSpaceAccessToken!;
}

export function useSocketListener(options: UseSocketListenerOptions) {
  const { onProcessed, enabled = true } = options;
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [workSpaceAccessToken, setWorkSpaceAccessToken] = useState<string | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 2;
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isIntentionallyDisconnectedRef = useRef(false);
  const connectWebSocketRef = useRef<(() => void) | null>(null);
  const logger = createLogger("useSocketListener");

  // Memoize the callback to prevent unnecessary re-renders
  const handleProcessed = useCallback(
    (data: string) => {
      if (onProcessed) {
        onProcessed(data);
      }
    },
    [onProcessed]
  );

  const connectWebSocket = useCallback(() => {
    if (!workSpaceAccessToken || isIntentionallyDisconnectedRef.current || !enabled || isConnecting || isConnected) {
      return;
    }

    // Close existing connection if any
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
      return;
    }

    const ws = new WebSocket(AppConfig.getInstance().WEBSOCKET_BASE_URL + "?accessToken=" + workSpaceAccessToken);
    wsRef.current = ws;
    setIsConnecting(true);

    ws.onopen = () => {
      setIsConnecting(false);
      setIsConnected(true);
      retryCountRef.current = 0; // Reset retry count on successful connection
    };

    ws.onmessage = event => {
      if (!event) {
        return;
      }
      handleProcessed(event.data);
    };

    ws.onclose = () => {
      setIsConnected(false);
      setIsConnecting(false);

      // Only attempt to reconnect if not intentionally disconnected and we haven't exceeded max retries
      if (!isIntentionallyDisconnectedRef.current && retryCountRef.current < maxRetries) {
        const delay = Math.pow(2, retryCountRef.current) * 1000; // Exponential backoff: 1s, 2s

        retryTimeoutRef.current = setTimeout(() => {
          retryCountRef.current++;
          if (connectWebSocketRef.current) {
            connectWebSocketRef.current();
          }
        }, delay);
      } else if (isIntentionallyDisconnectedRef.current) {
        logger.info("WebSocket intentionally disconnected, not attempting to reconnect");
      } else {
        logger.error("WebSocket connection failed after maximum retry attempts");
      }
    };

    ws.onerror = error => {
      logger.error("WebSocket error:", error);
      setIsConnected(false);
      setIsConnecting(false);
    };
  }, [workSpaceAccessToken, enabled, isConnecting, isConnected, handleProcessed]);

  // Store the latest version of connectWebSocket in a ref
  useEffect(() => {
    connectWebSocketRef.current = connectWebSocket;
  }, [connectWebSocket]);

  useEffect(() => {
    getWorkSpaceAccessToken().then(workSpaceAccessToken => {
      setWorkSpaceAccessToken(workSpaceAccessToken);
    });
  }, []);

  useEffect(() => {
    if (!workSpaceAccessToken || !enabled) {
      // If disabled, clean up any existing connection
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        logger.info("Disabling WebSocket connection");
        wsRef.current.close();
      }
      return;
    }

    const connectTimeout = setTimeout(() => {
      connectWebSocketRef.current?.();
    }, 0);

    return () => {
      clearTimeout(connectTimeout);
      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      // Close WebSocket connection
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [workSpaceAccessToken, enabled]);

  const disconnectWebSocket = useCallback(() => {
    // Mark as intentionally disconnected to prevent reconnection
    isIntentionallyDisconnectedRef.current = true;

    // Clear any pending retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
      setIsConnected(false);
      retryCountRef.current = 0;
      wsRef.current = null;
    }
  }, []);

  return {
    isConnecting,
    isConnected,
    disconnectWebSocket,
  };
}
